import * as yup from 'yup';

import { yupGermanNumberTransform } from '@/utils/numberParser';
import {
  createLteSiblingTest,
  nullableDate,
  numberToNull,
  toValidDateOrNull,
} from '@/utils/validation';

export const getOperatingCostInvoiceSchema = (t: (key: string) => string) =>
  yup.object({
    contractId: yup.string().required(t('errors.required')),
    pointOfSaleId: yup.string().required(t('errors.required')),
    issuerLegalName: yup.string().required(t('errors.required')),

    issueDate: nullableDate()
      .required(t('errors.required'))
      .when('periodEnd', (periodEnd, schema: yup.DateSchema<Date | null>) => {
        const cmp = toValidDateOrNull(periodEnd);
        if (!cmp) return schema;
        return schema.min(cmp, t('errors.issueDateBeforePeriodEnd'));
      }),

    periodStart: nullableDate().required(t('errors.required')),

    periodEnd: nullableDate()
      .required(t('errors.required'))
      .when('periodStart', (periodStart, schema: yup.DateSchema<Date | null>) => {
        const cmp = toValidDateOrNull(periodStart);
        if (!cmp) return schema;
        return schema.min(cmp, t('errors.periodEndBeforeStart'));
      }),

    documentReviewDeadline: nullableDate()
      .optional()
      .when('issueDate', (issueDate, schema: yup.DateSchema<Date | null | undefined>) => {
        const cmp = toValidDateOrNull(issueDate);
        if (!cmp) return schema;
        return schema.min(cmp, t('errors.dateBeforeIssueDate'));
      }),

    objectionDeadline: nullableDate()
      .optional()
      .when('issueDate', (issueDate, schema: yup.DateSchema<Date | null | undefined>) => {
        const cmp = toValidDateOrNull(issueDate);
        if (!cmp) return schema;
        return schema.min(cmp, t('errors.dateBeforeIssueDate'));
      }),

    internalObjectionDeadline: nullableDate()
      .optional()
      .when('issueDate', (issueDate, schema: yup.DateSchema<Date | null | undefined>) => {
        const cmp = toValidDateOrNull(issueDate);
        if (!cmp) return schema;
        return schema.min(cmp, t('errors.dateBeforeIssueDate'));
      })
      .when(
        'objectionDeadline',
        (objectionDeadline, schema: yup.DateSchema<Date | null | undefined>) => {
          const cmp = toValidDateOrNull(objectionDeadline);
          if (!cmp) return schema;
          return schema.min(cmp, t('errors.dateBeforeObjectionDeadline'));
        },
      ),

    customerPrepaymentNet: yup
      .number()
      .transform(yupGermanNumberTransform)
      .optional()
      .transform(numberToNull)
      .test(
        'net-lte-gross',
        t('errors.netGreaterThanGross'),
        createLteSiblingTest('customerPrepaymentGross'),
      ),
    customerPrepaymentGross: yup.number().transform(yupGermanNumberTransform).optional().nullable(),
    currency: yup.string().required(t('errors.required')),
    statedCustomerPrepaymentNet: yup
      .number()
      .transform(yupGermanNumberTransform)
      .optional()
      .nullable(),
    settlementResultNet: yup
      .number()
      .transform(yupGermanNumberTransform)
      .optional()
      .transform(numberToNull)
      .test(
        'net-lte-gross',
        t('errors.netGreaterThanGross'),
        createLteSiblingTest('settlementResultGross'),
      ),
    settlementResultGross: yup.number().transform(yupGermanNumberTransform).optional().nullable(),
    costsNet: yup.number().transform(yupGermanNumberTransform).optional().nullable(),
    vatRate: yup.number().transform(yupGermanNumberTransform).required(t('errors.required')),
    isHeatingCostsCompliantHKVO: yup.boolean().required(t('errors.required')),
  });
